"use client";
import { jsxs, jsx } from "react/jsx-runtime";
import DOMPurify from "isomorphic-dompurify";
import { isBC } from "../../../../../../utils/businessType.lib.js";
import { Box } from "@mui/material";
import { Text } from "@nxt-costco-com/forge-components";
import { ColorGray700 } from "@nxt-costco-com/forge-design-tokens";
import { EcoFee } from "../../../../EcoFee/index.js";
import { generateDiscountText, calculateSavings, displayPromoBreakdown } from "../../../../Price/lib.js";
import { PriceTooltip } from "../../../../PriceTooltip/index.js";
import { InstantSavings } from "./InstantSavings/index.js";
import { TaxableItem } from "../TaxableItem/index.js";
import { useProductTileData } from "../../../../../../hooks/useProductTileData/index.js";
import { Price } from "./Price/index.js";
import { getFormattedPrice } from "./Price/lib.js";
import { generateFreightText } from "./lib.js";
import { priceGroupContainerStyles, promoTextStyles } from "./styles.js";
const COMPONENT_NAME = "PriceGroup";
const PriceGroup = ({
  productTileMaxLineNumber = 3,
  cookieContext,
  enablePromoBreakdown
}) => {
  const {
    deliveryLocation: { state }
  } = cookieContext;
  const { normalizedData, translationsConfig, locale } = useProductTileData();
  const {
    discountsData,
    isEcoFeeEligible,
    priceListData,
    promoItem,
    productInfoData,
    savingsData,
    ecoFee
  } = normalizedData;
  const { promoText, promoTextColor } = promoItem || {};
  const { partNumber } = productInfoData;
  const promoTextColorStyles = { color: promoTextColor || ColorGray700 };
  const updatedPromoTextStyles = {
    ...promoTextColorStyles,
    ...promoTextStyles,
    WebkitLineClamp: productTileMaxLineNumber,
    lineClamp: productTileMaxLineNumber
  };
  const { translations, promoBreakdownCAProvince } = translationsConfig || {};
  let sanitizedPromoText = "";
  if (typeof window !== "undefined") {
    sanitizedPromoText = DOMPurify.sanitize(promoText);
  }
  if (priceListData) {
    sanitizedPromoText = generateDiscountText(
      sanitizedPromoText,
      translations == null ? void 0 : translations.discountregex,
      translations == null ? void 0 : translations.afterprefix
    );
  }
  const ecoFeeStatement = (translations == null ? void 0 : translations.ecofeelabel) || "";
  const instantSavingsText = (translations == null ? void 0 : translations.instantsavings) || "";
  const savingsObj = calculateSavings(
    locale,
    savingsData == null ? void 0 : savingsData.listPrice,
    savingsData == null ? void 0 : savingsData.salePrice
  );
  let originalPrice = typeof (savingsData == null ? void 0 : savingsData.listPrice) === "string" ? parseFloat(savingsData == null ? void 0 : savingsData.listPrice) : savingsData == null ? void 0 : savingsData.listPrice;
  if (originalPrice && (ecoFee == null ? void 0 : ecoFee.includeInPrice)) {
    originalPrice += parseFloat(ecoFee == null ? void 0 : ecoFee.includeInPrice) || 0;
  }
  const formattedListPrice = getFormattedPrice(locale, originalPrice);
  const isCostcoDirectSavings = isBC() && (savingsData == null ? void 0 : savingsData.freightSavingsEnabled) && (savingsData == null ? void 0 : savingsData.bundle) && (savingsData == null ? void 0 : savingsData.priceInCartOnly) && (savingsObj == null ? void 0 : savingsObj.displaySavings);
  const freightSavingsText = generateFreightText(
    translations == null ? void 0 : translations.freightsavings,
    savingsObj == null ? void 0 : savingsObj.savingsAmount
  );
  return /* @__PURE__ */ jsxs(
    Box,
    {
      "data-testid": `${COMPONENT_NAME}_${partNumber}`,
      sx: priceGroupContainerStyles,
      children: [
        /* @__PURE__ */ jsx(TaxableItem, {}),
        (priceListData == null ? void 0 : priceListData.fulfillmentTexts) && /* @__PURE__ */ jsx(
          PriceTooltip,
          {
            id: partNumber,
            priceListData
          }
        ),
        priceListData && /* @__PURE__ */ jsx(
          Price,
          {
            ...priceListData,
            locale,
            uniqueId: partNumber
          }
        ),
        /* @__PURE__ */ jsx(EcoFee, { locale, priceValue: priceListData == null ? void 0 : priceListData.priceValue }),
        isEcoFeeEligible && !normalizedData.linkFeePrices && /* @__PURE__ */ jsx(Text, { uniqueId: `ecoFee_${partNumber}`, variant: "t6", children: ecoFeeStatement }),
        isCostcoDirectSavings && /* @__PURE__ */ jsx(
          Text,
          {
            sx: updatedPromoTextStyles,
            uniqueId: `savingsText_${partNumber}`,
            variant: "t6",
            children: freightSavingsText
          }
        ),
        promoText && /* @__PURE__ */ jsx(
          Text,
          {
            sx: updatedPromoTextStyles,
            uniqueId: `promoText_${partNumber}`,
            variant: "t6",
            children: /* @__PURE__ */ jsx(
              "span",
              {
                dangerouslySetInnerHTML: {
                  __html: sanitizedPromoText
                }
              }
            )
          }
        ),
        /* @__PURE__ */ jsx(
          InstantSavings,
          {
            displayDiscount: discountsData == null ? void 0 : discountsData.displayDiscount,
            instantSavingsText,
            locale,
            partNumber,
            totalDiscount: discountsData == null ? void 0 : discountsData.totalDiscount
          }
        ),
        displayPromoBreakdown(
          enablePromoBreakdown,
          priceListData,
          locale,
          promoBreakdownCAProvince,
          savingsObj == null ? void 0 : savingsObj.displaySavings
        ) && /* @__PURE__ */ jsxs(
          Text,
          {
            sx: updatedPromoTextStyles,
            uniqueId: `listPrice_${partNumber}`,
            variant: "t6",
            children: [
              translations == null ? void 0 : translations.waspriceprefix,
              " ",
              formattedListPrice
            ]
          }
        )
      ]
    }
  );
};
export {
  PriceGroup
};
